import { NetworkStatus } from "@apollo/client";
import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import usePolledQuery from "apollo/usePolledQuery";
import NotFoundPage from "components/error/NotFoundPage";
import useSortState from "components/Filters/useSortState";
import PageLayoutSkeleton from "components/PageLayoutSkeleton";
import { URL_SEARCH_KEY, URL_SORT_DIRECTION, URL_SORT_KEY } from "constants/url_query_keys";
import useErrorHandle from "hooks/useErrorHandle";

import { PollingIntervalGroups } from "../../../apollo/constants";
import SharedModulesEmptyState from "./components/EmptyState";
import SharedModulesPageLayout from "./components/PageLayout";
import SharedModulesTable from "./components/Table";
import { FILTERS_ORDER_SETTINGS_KEY, initialSortDirection, initialSortOption } from "./constants";
import { GET_SHARED_MODULES_V2 } from "./gql";
import { filterAndSortModules, processSharedModules } from "./helpers";

const SharedModulesNew = () => {
  const { sortState, applySortState } = useSortState();

  const { error, loading, data, networkStatus } = usePolledQuery(GET_SHARED_MODULES_V2, {
    pollingGroup: PollingIntervalGroups.Lists,
    // avoid request executing twice while fetchMore
    nextFetchPolicy: "cache-first",
    // APOLLO CLIENT UPDATE
  });

  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get(URL_SEARCH_KEY);

  const ErrorContent = useErrorHandle(error);
  const initialLoading = loading && networkStatus === NetworkStatus.loading;

  const modules = useMemo(() => processSharedModules(data), [data]);

  useEffect(() => {
    if (searchParams.get(URL_SORT_KEY) || searchParams.get(URL_SORT_DIRECTION)) return;
    // set default sorting when there is no sorting set
    applySortState(
      { sortKey: initialSortOption, sortDirection: initialSortDirection },
      FILTERS_ORDER_SETTINGS_KEY
    );
  }, [searchParams, applySortState]);

  const modulesFiltered = useMemo(() => {
    return filterAndSortModules(modules, searchQuery, sortState);
  }, [modules, searchQuery, sortState]);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (initialLoading && !data?.sharedModulesV2) {
    return (
      <SharedModulesPageLayout>
        <PageLayoutSkeleton />
      </SharedModulesPageLayout>
    );
  }

  if (networkStatus !== NetworkStatus.refetch && !loading && !data?.sharedModulesV2) {
    return <NotFoundPage />;
  }

  return (
    <SharedModulesPageLayout>
      {data && !modulesFiltered.length && (
        <SharedModulesEmptyState didPerformSearch={!!searchQuery} />
      )}

      {data && modulesFiltered.length > 0 && (
        <SharedModulesTable sharedModules={modulesFiltered} loadingContent={initialLoading} />
      )}
    </SharedModulesPageLayout>
  );
};

export default SharedModulesNew;
